import { type JSX, useCallback, useRef, useState } from 'react';
import { FormProvider, useForm, type DefaultValues, type FieldErrors, type FieldPath } from 'react-hook-form';
import { _get, _notNil } from '@/littledash';
import type { VerticalStepFormProps } from './VerticalStepForm.model';
import { StepFormEngine } from './VerticalStepFormEngine';
import { flattenSelectValues } from './VerticalStepForm.utils';
import { StepFormProvider } from './VerticalStepFormContext';
import FormShell from './FormShell/FormShell';
import StepSection from './StepSection/StepSection';

/**
 * Config-driven vertical step form. Renders sequential collapsible steps,
 * each containing fields resolved from a registry. Manages progression,
 * validation, and submission via an internal StepFormEngine.
 */
const VerticalStepForm = <T extends Record<string, unknown>>({
  steps,
  defaultValues,
  onSubmit,
  title,
  description,
  submitDescription,
}: VerticalStepFormProps<T>): JSX.Element => {
  const engineRef = useRef(new StepFormEngine(steps));
  const formMethods = useForm<T>({
    defaultValues: defaultValues as DefaultValues<T>,
    mode: 'onTouched',
    shouldUnregister: false,
  });
  const { handleSubmit, trigger, resetField } = formMethods;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAdvance = useCallback(
    async (stepId: string): Promise<void> => {
      const step = steps.find((s) => s.id === stepId);
      const stepFields = step?.fields.map((f) => f.name as FieldPath<T>) ?? [];
      const valid = stepFields.length > 0 ? await trigger(stepFields) : true;
      if (valid) {
        engineRef.current.advance(stepId);
      }
    },
    [steps, trigger]
  );

  const handleSkip = useCallback(
    (stepId: string): void => {
      const step = steps.find((s) => s.id === stepId);
      step?.fields.forEach((field) => resetField(field.name as FieldPath<T>));
      engineRef.current.skip(stepId);
    },
    [steps, resetField]
  );

  const handleGoToStep = useCallback((stepId: string): void => {
    engineRef.current.goToStep(stepId);
  }, []);

  const handleValueChange = useCallback((stepId: string): void => {
    engineRef.current.uncomplete(stepId);
  }, []);

  const handleFormSubmit = useCallback(
    async (values: T): Promise<void> => {
      setIsSubmitting(true);
      try {
        await onSubmit(flattenSelectValues(values, steps));
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, steps]
  );

  const onSubmitError = useCallback(
    (errors: FieldErrors<T>): void => {
      const firstErrorStep = steps.find((step) => step.fields.some((field) => _notNil(_get(errors, field.name))));
      if (firstErrorStep) {
        engineRef.current.goToStep(firstErrorStep.id);
      }
    },
    [steps]
  );

  return (
    <StepFormProvider value={{ engine: engineRef.current }}>
      <FormProvider {...formMethods}>
        <FormShell
          title={title}
          description={description}
          steps={steps}
          submitDescription={submitDescription}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit(handleFormSubmit, onSubmitError)}
        >
          {steps.map((step, index) => (
            <StepSection
              key={step.id}
              config={step}
              stepNumber={index + 1}
              onAdvance={handleAdvance}
              onSkip={handleSkip}
              onGoToStep={handleGoToStep}
              onValueChange={handleValueChange}
            />
          ))}
        </FormShell>
      </FormProvider>
    </StepFormProvider>
  );
};

export default VerticalStepForm;
